import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiToken } from '../auth/AuthProvider';
import {
  getReportDetail, updateReport, downloadReport,
  ReportMetadata, ReceiptMetadata, ExpenseLineItem,
} from '../services/api';
import { deriveCategory, getCategoryStyle, receiptToLineItem } from '../services/categoryUtils';
import { useAppSettings } from '../services/AppSettingsContext';
import ReviewLineItemsStep from './new-report/ReviewLineItemsStep';
import SelectReceiptsStep from './new-report/SelectReceiptsStep';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import LoadingAnimation from '../components/LoadingAnimation';

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a365d', margin: '0 0 4px' },
  subtitle: { color: '#666', margin: 0, fontSize: 14 },
  downloadActions: { display: 'flex', gap: 8 },
  btn: { background: '#0078d4', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  btnOutline: { background: 'white', color: '#0078d4', border: '1px solid #0078d4', padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  backLink: { color: '#0078d4', cursor: 'pointer', fontSize: 14, marginBottom: 16, display: 'inline-block', background: 'none', border: 'none', padding: 0 },
  section: { marginBottom: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#1a365d', margin: '0 0 12px' },
  receiptGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 },
  receiptCard: { border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: 'white', cursor: 'pointer' },
  previewImg: { width: '100%', height: 105, objectFit: 'cover' as const, background: '#f7fafc' },
  imageFallback: { width: '100%', height: 105, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardInfo: { padding: '6px 8px' },
  cardVendor: { fontSize: 11, fontWeight: 600, color: '#1a365d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  cardDetail: { fontSize: 10, color: '#666' },
  cardAmount: { fontSize: 12, fontWeight: 700, color: '#38a169' },
  success: { background: '#c6f6d5', border: '1px solid #38a169', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#22543d', fontSize: 14, fontWeight: 500 },
};

export default function ReportDetailPage() {
  const { mileageRate } = useAppSettings();
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const getToken = useApiToken();

  const [report, setReport] = useState<ReportMetadata | null>(null);
  const [expenses, setExpenses] = useState<ExpenseLineItem[]>([]);
  const [receipts, setReceipts] = useState<ReceiptMetadata[]>([]);
  const [employeeName, setEmployeeName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [previewReceipt, setPreviewReceipt] = useState<ReceiptMetadata | null>(null);
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());
  const [showAddReceipts, setShowAddReceipts] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current || !reportId) return;
    loadedRef.current = true;
    (async () => {
      try {
        const token = await getToken();
        const detail = await getReportDetail(token, reportId);
        setReport(detail.report);
        setExpenses(detail.expenses);
        setReceipts(detail.receipts);
        setEmployeeName(detail.employee_name);
      } catch (e) {
        console.error('Failed to load report', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken, reportId]);

  const handleRegenerate = async () => {
    if (!report || !reportId) return;
    setSaving(true);
    setSuccess(false);
    try {
      const token = await getToken();
      const updated = await updateReport(token, reportId, {
        employee_name: employeeName,
        title: report.title,
        date_range_start: report.date_range_start,
        date_range_end: report.date_range_end,
        expenses,
        receipt_ids: receipts.map(r => r.receipt_id),
      });
      setReport(updated);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (e) {
      alert(`Failed to regenerate: ${e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (format: 'pdf' | 'xlsx') => {
    if (!reportId) return;
    try {
      const token = await getToken();
      await downloadReport(token, reportId, format);
    } catch (e) {
      alert(`Download failed: ${e}`);
    }
  };

  if (loading) return <div style={{ padding: 24 }}><LoadingAnimation /></div>;
  if (!report) return <p style={{ padding: 24, color: '#999' }}>Report not found.</p>;

  return (
    <div>
      <button style={styles.backLink} onClick={() => navigate('/history')}>
        &larr; Back to History
      </button>

      {success && (
        <div style={styles.success}>
          Report regenerated successfully! Download the updated files below.
        </div>
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{report.title || '(untitled)'}</h1>
          <p style={styles.subtitle}>
            {report.date_range_start} to {report.date_range_end} &middot; Generated {new Date(report.generated_timestamp).toLocaleDateString()}
          </p>
        </div>
        <div style={styles.downloadActions}>
          <button style={styles.btn} onClick={() => handleDownload('pdf')}>Download PDF</button>
          <button style={styles.btnOutline} onClick={() => handleDownload('xlsx')}>Download Excel</button>
        </div>
      </div>

      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Receipts ({receipts.length})</h2>
          <button
            style={showAddReceipts ? styles.btnOutline : styles.btn}
            onClick={() => setShowAddReceipts(!showAddReceipts)}
          >
            {showAddReceipts ? 'Close' : '+ Add Receipts'}
          </button>
        </div>
        {receipts.length === 0 ? (
          <p style={{ color: '#999', fontSize: 13, fontStyle: 'italic' }}>No receipts attached to this report.</p>
        ) : (
          <div style={styles.receiptGrid}>
            {receipts.map((r) => {
              const fn = r.file_name.toLowerCase();
              const isPdf = fn.endsWith('.pdf') || fn.endsWith('.html');
              const hasImgError = imgErrors.has(r.receipt_id);
              const category = deriveCategory(r);
              const cat = getCategoryStyle(category);
              return (
                <div key={r.receipt_id} style={{ ...styles.receiptCard, background: cat.cardBg, borderColor: cat.border }} onClick={() => setPreviewReceipt(r)}>
                  {/* Category label at top */}
                  <div style={{ background: cat.bg, padding: '3px 8px' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: cat.text }}>{cat.label}</span>
                  </div>
                  {isPdf || hasImgError ? (
                    <div style={{...styles.imageFallback, background: cat.bg}}>
                      <span style={{ fontSize: 36 }}>{cat.icon}</span>
                    </div>
                  ) : (
                    <img
                      src={r.download_url}
                      alt={r.vendor_name || cat.label}
                      style={styles.previewImg}
                      onError={() => setImgErrors(prev => new Set(prev).add(r.receipt_id))}
                    />
                  )}
                  <div style={styles.cardInfo}>
                    <div style={styles.cardVendor}>{r.vendor_name || 'Unknown'}</div>
                    <div style={styles.cardDetail}>{r.receipt_date || 'No date'}</div>
                    {r.receipt_amount != null && (
                      <div style={styles.cardAmount}>${r.receipt_amount.toFixed(2)}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showAddReceipts && (
          <div style={{ marginTop: 16, padding: 16, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f7fafc' }}>
            <SelectReceiptsStep
              startDate={report.date_range_start}
              endDate={report.date_range_end}
              receipts={receipts}
              preSelectedIds={receipts.map(r => r.receipt_id)}
              onReceiptsChange={setReceipts}
              onNext={(selectedReceipts) => {
                // Auto-create line items for any receipts not already wired to one
                const existingReceiptIds = new Set(expenses.filter(e => e.receipt_id).map(e => e.receipt_id));
                const reason = expenses[0]?.reason || 'Conference Attendee';
                const newItems = selectedReceipts
                  .filter(r => !existingReceiptIds.has(r.receipt_id))
                  .map(r => receiptToLineItem(r, reason, mileageRate));
                if (newItems.length > 0) {
                  setExpenses([...expenses, ...newItems]);
                }
                setShowAddReceipts(false);
              }}
              onBack={() => setShowAddReceipts(false)}
              nextLabel="Done"
              backLabel="Cancel"
            />
          </div>
        )}
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Line Items</h2>
        <ReviewLineItemsStep
          expenses={expenses}
          receipts={receipts}
          reason={expenses[0]?.reason || 'Conference Attendee'}
          startDate={report?.date_range_start}
          endDate={report?.date_range_end}
          onChange={setExpenses}
          onNext={handleRegenerate}
          onBack={() => navigate('/history')}
          nextLabel="Save & Regenerate"
          backLabel="Back to History"
          saving={saving}
        />
      </div>

      <ReceiptPreviewModal receipt={previewReceipt} onClose={() => setPreviewReceipt(null)} />
    </div>
  );
}
